import Swal, { SweetAlertIcon, SweetAlertResult } from "sweetalert2";

export interface ConfirmationOptions {
  title?: string;
  text?: string;
  icon?: SweetAlertIcon;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonColor?: string;
  cancelButtonColor?: string;
  showCancelButton?: boolean;
  reverseButtons?: boolean;
  focusCancel?: boolean;
  id?: string;
  customClass?: Record<string, string>;
}

export interface SuccessOptions {
  title?: string;
  text?: string;
  icon?: SweetAlertIcon;
  confirmButtonText?: string;
  timer?: number;
  showConfirmButton?: boolean;
}

export interface ErrorOptions {
  title?: string;
  text?: string;
  icon?: SweetAlertIcon;
  confirmButtonText?: string;
  confirmButtonColor?: string;
}

/**
 * Custom hook for handling confirmation dialogs, success messages, and error alerts
 * Uses SweetAlert2 for consistent UI across the application
 */
export const useConfirmation = () => {
  /**
   * Show a confirmation dialog for delete operations
   */
  const confirmDelete = async (
    options: Partial<ConfirmationOptions> = {},
  ): Promise<boolean> => {
    const itemName = options.title || "item";
    const result: SweetAlertResult = await Swal.fire({
      title: `Delete ${itemName}`,
      text: options.text || `You won't be able to recover this ${itemName}!`,
      icon: options.icon || undefined,
      showCancelButton: options.showCancelButton ?? true,
      confirmButtonColor: options.confirmButtonColor || "#d33",
      cancelButtonColor: options.cancelButtonColor || "#3085d6",
      confirmButtonText: options.confirmButtonText || "Delete",
      cancelButtonText: options.cancelButtonText || "Cancel",
      reverseButtons: options.reverseButtons ?? true,
      focusCancel: options.focusCancel ?? true,
      customClass: {
        actions: "emerald-button mt-0",
        cancelButton: "mdc-button primary custom-outlined-button",
        confirmButton: `mdc-button accent custom-flat-button ${options?.id}`,
      },
    });

    return result.isConfirmed;
  };

  /**
   * Show a confirmation dialog for unsaved changes
   */
  const confirmUnsavedChanges = async (
    options: Partial<ConfirmationOptions> = {},
  ): Promise<boolean> => {
    const result: SweetAlertResult = await Swal.fire({
      title: options.title || "Unsaved Changes",
      text:
        options.text ||
        "You have unsaved changes. Are you sure you want to leave?",
      icon: options.icon || "warning",
      showCancelButton: options.showCancelButton ?? true,
      confirmButtonColor: options.confirmButtonColor || "#d33",
      cancelButtonColor: options.cancelButtonColor || "#3085d6",
      confirmButtonText: options.confirmButtonText || "Leave without saving",
      cancelButtonText: options.cancelButtonText || "Stay and save",
      reverseButtons: options.reverseButtons ?? true,
      focusCancel: options.focusCancel ?? true,
    });

    return result.isConfirmed;
  };

  /**
   * Show a generic confirmation dialog
   */
  const confirmAction = async (
    title: string,
    text?: string,
    options: Partial<ConfirmationOptions> = {},
  ): Promise<boolean> => {
    const result: SweetAlertResult = await Swal.fire({
      title: options.title || title,
      text: options.text || text,
      icon: options.icon || "question",
      showCancelButton: options.showCancelButton ?? true,
      confirmButtonColor: options.confirmButtonColor || "#3085d6",
      cancelButtonColor: options.cancelButtonColor || "#6c757d",
      confirmButtonText: options.confirmButtonText || "Confirm",
      cancelButtonText: options.cancelButtonText || "Cancel",
      reverseButtons: options.reverseButtons ?? false,
      focusCancel: options.focusCancel ?? false,
      customClass: options.customClass,
    });

    return result.isConfirmed;
  };

  /**
   * Show a success message
   */
  const showSuccess = async (
    title: string,
    text?: string,
    options: Partial<SuccessOptions> = {},
  ): Promise<void> => {
    await Swal.fire({
      title: options.title || title,
      text: options.text || text,
      icon: options.icon || "success",
      confirmButtonText: options.confirmButtonText || "OK",
      timer: options.timer,
      showConfirmButton: options.showConfirmButton ?? !options.timer,
    });
  };

  /**
   * Show an error message
   */
  const showError = async (
    title: string,
    text?: string,
    options: Partial<ErrorOptions> = {},
  ): Promise<void> => {
    await Swal.fire({
      title: options.title || title,
      text: options.text || text,
      icon: options.icon || "error",
      confirmButtonText: options.confirmButtonText || "OK",
      confirmButtonColor: options.confirmButtonColor || "#d33",
    });
  };

  /**
   * Show an info message
   */
  const showInfo = async (
    title: string,
    text?: string,
    options: Partial<SuccessOptions> = {},
  ): Promise<void> => {
    await Swal.fire({
      title: options.title || title,
      text: options.text || text,
      icon: options.icon || "info",
      confirmButtonText: options.confirmButtonText || "OK",
      timer: options.timer,
      showConfirmButton: options.showConfirmButton ?? !options.timer,
    });
  };

  /**
   * Show a loading dialog
   */
  const showLoading = async (
    title: string = "Loading...",
    text?: string,
  ): Promise<void> => {
    Swal.fire({
      title,
      text,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
  };

  /**
   * Close any open dialog
   */
  const closeDialog = (): void => {
    Swal.close();
  };

  return {
    confirmDelete,
    confirmUnsavedChanges,
    confirmAction,
    showSuccess,
    showError,
    showInfo,
    showLoading,
    closeDialog,
  };
};

export default useConfirmation;
